/** RFC 0345 3j: removing a person from a project, session or share group.
 * People whose grant the person signed are adopted first; this writes
 * replacement grants from this device, so the caller must establish that the
 * device belongs to an owner. The person's agents leave with their issuer
 * groups (agent-grants.ts) unless another person's issuer group grants them. */
import {
  Access,
  type Capability,
  type DocumentId,
  type GroupId,
  type Keyhive,
  MemberedId,
  type SignedDelegation,
} from '@automerge/automerge-repo-keyhive'
import { type IssuerGroup, issuerGroups } from './agent-grants'

const hex = (bytes: Uint8Array) =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')

function delegatedKeys(grants: Capability[], signers: ReadonlySet<string>) {
  const known = new Set(signers)
  const keys: Array<{ grant: Capability; depth: number }> = []
  for (let grew = true; grew; ) {
    grew = false
    for (const grant of grants) {
      if (!grant.who.isIndividual) continue
      const id = hex(grant.who.id.toBytes())
      if (keys.some((key) => hex(key.grant.who.id.toBytes()) === id)) continue
      const signer = hex(grant.proof.verifyingKey)
      if (!known.has(signer)) continue
      const parent = keys.find(
        (key) => hex(key.grant.who.id.toBytes()) === signer,
      )
      keys.push({ grant, depth: parent ? parent.depth + 1 : 0 })
      known.add(id)
      grew = true
    }
  }
  return keys.sort((a, b) => b.depth - a.depth)
}

function proofViaGroup(grant: Capability, groupHex: string) {
  let proof: SignedDelegation | undefined = grant.proof
  while (proof) {
    if (hex(proof.delegation.delegate.id.toBytes()) === groupHex) return true
    proof = proof.delegation.proof
  }
  return false
}

interface RemovalPlan {
  dependents: Capability[]
  /** Keys handed on, directly on the target, by agents that leave. */
  keys: Array<{ grant: Capability; depth: number }>
  signerKeys: Set<string>
  /** The removed person's issuer groups on the target (3e). */
  issuers: IssuerGroup[]
  agentsLeaving: string[]
  agentsStaying: Array<{ key: string; grantedBy: string[] }>
  /** Why the removal must not run, before anything changes. */
  blocked?: string
}

async function removalGrants(
  hive: Keyhive,
  targetId: GroupId,
  removingId: GroupId,
  /** Keys the caller knows as its own pending invite keys. */
  trustedKeys: ReadonlySet<string> = new Set(),
): Promise<RemovalPlan> {
  const target = await hive.getGroup(targetId)
  const removing = await hive.getGroup(removingId)
  if (!target || !removing) throw new Error('Group grants are unavailable')
  const removingHex = hex(removingId.toBytes())
  const signedBy = new Set(
    (await removing.transitiveMembers())
      .filter((member) => member.who.isIndividual)
      .map((member) => hex(member.who.id.toBytes())),
  )
  if (!signedBy.size)
    throw new Error('Removed person’s signing devices are unavailable')
  const grants = await target.members()
  if (
    !grants.some(
      (grant) =>
        grant.who.isGroup && hex(grant.who.id.toBytes()) === removingHex,
    )
  )
    throw new Error('Removed person has no direct grant on this group')
  const allIssuers = await issuerGroups(hive, targetId)
  const issuerIds = new Set(allIssuers.map((group) => group.group))
  const issuers = allIssuers.filter((group) => group.person === removingHex)
  const others = allIssuers.filter((group) => group.person !== removingHex)
  const dependents = grants.filter(
    (grant) =>
      grant.who.isGroup &&
      hex(grant.who.id.toBytes()) !== removingHex &&
      !issuerIds.has(hex(grant.who.id.toBytes())) &&
      signedBy.has(hex(grant.proof.verifyingKey)),
  )
  const agentsLeaving: string[] = []
  const agentsStaying: RemovalPlan['agentsStaying'] = []
  for (const key of new Set(
    issuers.flatMap((group) => group.agents.map((agent) => agent.key)),
  )) {
    const grantedBy = [
      ...new Set(
        others
          .filter((group) => group.agents.some((agent) => agent.key === key))
          .map((group) => group.person),
      ),
    ].sort()
    if (grantedBy.length) agentsStaying.push({ key, grantedBy })
    else agentsLeaving.push(key)
  }
  agentsLeaving.sort()
  // Keyhive does not reliably cascade a direct key grant already known to a
  // warm peer. Revoke keys a leaving agent handed on, explicitly.
  const keys = delegatedKeys(grants, new Set(agentsLeaving))
  const swept = new Set(keys.map((key) => hex(key.grant.who.id.toBytes())))
  // A direct key grant is the older shape. Keyhive keeps one capability per
  // key and target and shows one signer, which may not be this person's even
  // when they granted it too: revoking them can take the other grant, and
  // keeping it can leave theirs. Refuse, unless the key is one this person's
  // agents handed on (swept above) or the caller's own pending invite key.
  const legacy = grants.filter((grant) => {
    // A Relay grant (the relay's own key) reads nothing.
    if (!grant.who.isIndividual || !grant.can.isReader) return false
    const key = hex(grant.who.id.toBytes())
    if (swept.has(key)) return false
    if (signedBy.has(hex(grant.proof.verifyingKey))) return true
    return !grant.can.equals(Access.admin()) && !trustedKeys.has(key)
  })
  const signerKeys = new Set([...signedBy, ...agentsLeaving])
  for (const key of keys) signerKeys.add(hex(key.grant.who.id.toBytes()))
  return {
    dependents,
    keys,
    signerKeys,
    issuers,
    agentsLeaving,
    agentsStaying,
    ...(legacy.length
      ? {
          blocked:
            'A key is granted directly here, the older way (an agent key or an unredeemed invite). Keyhive cannot tell who granted it, so removing this person could take someone else’s grant of it. Revoke that key first.',
        }
      : {}),
  }
}

/** Direct key grants on documents reached through this group need their own
 * sweep: Keyhive does not cascade one already known to a warm peer. */
async function documentKeys(
  hive: Keyhive,
  targetId: GroupId,
  signerKeys: Set<string>,
): Promise<Array<{ document: DocumentId; keys: Capability[] }>> {
  const target = hex(targetId.toBytes())
  const found: Array<{ document: DocumentId; keys: Capability[] }> = []
  for (const summary of await hive.reachableDocs()) {
    const document = summary.doc.docId
    const grants = await summary.doc.members()
    if (
      !grants.some(
        (grant) => grant.who.isGroup && hex(grant.who.id.toBytes()) === target,
      )
    )
      continue
    const signedKeys = delegatedKeys(grants, signerKeys).map(
      ({ grant }) => grant,
    )
    // Keyhive may select either valid authority path for the same direct key
    // grant. With another group on this document, a revoke could erase an
    // independent grant, while skipping it could leave target access alive.
    if (
      signedKeys.length &&
      grants.some(
        (grant) => grant.who.isGroup && hex(grant.who.id.toBytes()) !== target,
      )
    )
      throw new Error(
        'Document key grant has another group path; removal is ambiguous',
      )
    const keys = signedKeys.filter((grant) => proofViaGroup(grant, target))
    if (keys.length) found.push({ document, keys })
  }
  return found
}

/** The signed people whose group grants depend directly on the member being
 * removed. This reads Keyhive policy, never the mutable invitation ledger. */
export async function directInviteesForRemoval(
  hive: Keyhive,
  targetId: GroupId,
  removingId: GroupId,
): Promise<Array<{ id: string; level: number }>> {
  const found = new Map<string, number>()
  for (const grant of (await removalGrants(hive, targetId, removingId))
    .dependents) {
    const id = hex(grant.who.id.toBytes())
    found.set(id, Math.max(found.get(id) ?? 0, grant.can.level))
  }
  return [...found]
    .map(([id, level]) => ({ id, level }))
    .sort((a, b) => a.id.localeCompare(b.id))
}

export async function adoptDirectInvitees(
  hive: Keyhive,
  targetId: GroupId,
  removingId: GroupId,
  alsoRemove: ReadonlySet<string> = new Set(),
): Promise<string[]> {
  const adopted: string[] = []
  for (const grant of (await removalGrants(hive, targetId, removingId))
    .dependents) {
    const id = hex(grant.who.id.toBytes())
    if (alsoRemove.has(id) || adopted.includes(id)) continue
    await hive.addMember(
      grant.who.id,
      MemberedId.group(targetId),
      grant.can,
      [],
    )
    adopted.push(id)
  }
  return adopted
}

export interface MemberRemovalPreview {
  /** Why removal is refused; nothing changes when this is set. */
  blocked?: string
  /** Agent keys that lose the target with the person. */
  agentsLeaving: string[]
  /** Agent keys that stay, and the people whose own grant keeps them. */
  agentsStaying: Array<{ key: string; grantedBy: string[] }>
  /** People the person invited: kept unless also removed. */
  invitees: Array<{ id: string; level: number }>
}

/** What removing this person from a project or session group would do, read
 * from signed grants and changing nothing. */
export async function previewGroupMemberRemoval(
  hive: Keyhive,
  targetId: GroupId,
  removingId: GroupId,
  trustedKeys: ReadonlySet<string> = new Set(),
): Promise<MemberRemovalPreview> {
  const plan = await removalGrants(hive, targetId, removingId, trustedKeys)
  let blocked = plan.blocked
  if (!blocked)
    try {
      await documentKeys(hive, targetId, plan.signerKeys)
    } catch (error) {
      blocked = (error as Error).message
    }
  return {
    ...(blocked ? { blocked } : {}),
    agentsLeaving: plan.agentsLeaving,
    agentsStaying: plan.agentsStaying,
    invitees: await directInviteesForRemoval(hive, targetId, removingId),
  }
}

/** The Keyhive half of removing a member (3j): adopt kept invitees, revoke the
 * ones also removed, the keys the person's leaving agents handed on, the
 * person's issuer groups, then the person. Another person's issuer group
 * keeps an agent both granted. Callers rotate keys and sync after. */
export async function revokeGroupMemberAndDirectInvitees(
  hive: Keyhive,
  targetId: GroupId,
  removingId: GroupId,
  alsoRemove: ReadonlySet<string> = new Set(),
  trustedKeys: ReadonlySet<string> = new Set(),
): Promise<{
  adopted: string[]
  removed: string[]
  agentsRemoved: string[]
  agentsKept: string[]
}> {
  const removingHex = hex(removingId.toBytes())
  if (alsoRemove.has(removingHex))
    throw new Error('The removed member is already selected')
  const plan = await removalGrants(hive, targetId, removingId, trustedKeys)
  if (plan.blocked) throw new Error(plan.blocked)
  const { dependents, keys, signerKeys, issuers } = plan
  const documents = await documentKeys(hive, targetId, signerKeys)
  const selected = new Map(
    dependents
      .filter((grant) => alsoRemove.has(hex(grant.who.id.toBytes())))
      .map((grant) => [hex(grant.who.id.toBytes()), grant.who.id]),
  )
  if (selected.size !== alsoRemove.size)
    throw new Error('An additional member has no direct grant on this group')
  // Every adoption must finish before any revoke. If it fails, the removed
  // person still holds their grant and the caller can retry safely.
  const adopted = await adoptDirectInvitees(
    hive,
    targetId,
    removingId,
    alsoRemove,
  )
  const removed: string[] = []
  for (const { document, keys: directKeys } of documents)
    for (const key of directKeys)
      await hive.revokeMember(key.who.id, true, MemberedId.document(document))
  for (const key of keys) {
    await hive.revokeMember(key.grant.who.id, true, MemberedId.group(targetId))
  }
  for (const [id, who] of selected) {
    await hive.revokeMember(who, true, MemberedId.group(targetId))
    removed.push(id)
  }
  for (const group of issuers)
    await hive.revokeMember(
      group.id.toIdentifier(),
      true,
      MemberedId.group(targetId),
    )
  await hive.revokeMember(
    removingId.toIdentifier(),
    true,
    MemberedId.group(targetId),
  )
  removed.push(removingHex)
  return {
    adopted,
    removed,
    agentsRemoved: plan.agentsLeaving,
    agentsKept: plan.agentsStaying.map((agent) => agent.key),
  }
}
